import { createContext, useContext, useState, useEffect } from "react";
import { useUser as useClerkUser } from "@clerk/clerk-react";
import { api } from "../lib/api";
import type { Permission } from "../services/authService";

const logger = {
  info: (...args: any[]) => console.log("[AuthContext]", ...args),
  error: (...args: any[]) => console.error("[AuthContext]", ...args),
};

/**
 * User interface with role information from database (single source of truth)
 * Feature: 001-user-role-management (User Story 1 - T039)
 * Security: Role is always fetched from backend database, not from Clerk metadata
 */
interface User {
  id: string;
  email: string;
  role: string;
  clerkId?: string;
  displayName?: string;
  isAdmin: boolean;
  permissions: Permission[];
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  /**
   * Whether authentication and role are being loaded
   * Feature: 001-user-role-management (T039)
   */
  isLoading: boolean;
  /**
   * User role from database
   * Feature: 001-user-role-management (T039)
   */
  role: string | null;
  /**
   * Check if user is an admin
   * Feature: 001-user-role-management (T039)
   */
  isAdmin: boolean;
  /**
   * User permissions from database
   * Feature: 001-user-role-management (T039)
   */
  permissions: Permission[];
  /**
   * Clerk user object
   * Feature: 001-user-role-management (T039)
   */
  clerkUser: ReturnType<typeof useClerkUser>['user'];
  /**
   * Whether the user's account is blocked
   */
  isBlocked: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoadingRole, setIsLoadingRole] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const { user: clerkUser, isLoaded: isClerkLoaded } = useClerkUser();

  // Get user role and blocked status from Clerk JWT token metadata (synced from database)
  // Feature: 001-user-role-management (T039)
  // Security:
  // - Role and is_blocked are stored in Clerk public metadata (synced from database)
  // - JWT is cryptographically signed by Clerk, so metadata cannot be tampered with
  // - Backend still validates permissions against database for critical operations
  // - This approach is fast (no extra API call) and secure (JWT validation)
  useEffect(() => {
    const loadUser = async () => {
      if (!clerkUser) {
        setUser(null);
        setIsBlocked(false);
        return;
      }

      try {
        setIsLoadingRole(true);

        // Extract role and blocked status from Clerk public metadata (included in JWT)
        // These are synced to Clerk when assigned/changed in our database
        const role = (clerkUser.publicMetadata?.role as string) || 'user';
        const isAdmin = role === 'admin';
        const blocked = clerkUser.publicMetadata?.is_blocked === true;

        // Set blocked status immediately from metadata
        setIsBlocked(blocked);
        if (blocked) {
          logger.info(`User is blocked: email=${clerkUser.primaryEmailAddress?.emailAddress}`);
        }

        // For permissions, we still need to fetch from backend if needed
        // But for basic role-based UI, we can use the JWT metadata
        const syncedUser: User = {
          id: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress || '',
          role: role,
          clerkId: clerkUser.id,
          displayName: clerkUser.fullName || clerkUser.firstName || undefined,
          isAdmin: isAdmin,
          permissions: [], // Can be fetched lazily if needed for granular permissions
        };

        setUser(syncedUser);
        logger.info(`User loaded from JWT: email=${syncedUser.email}, role=${role}, is_blocked=${blocked}`);
      } catch (error) {
        console.error('Error loading user from Clerk:', error);

        // Fallback: create basic user object without role
        const fallbackUser: User = {
          id: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress || '',
          role: 'user', // Default to user role on error
          clerkId: clerkUser.id,
          displayName: clerkUser.fullName || clerkUser.firstName || undefined,
          isAdmin: false,
          permissions: [],
        };

        setUser(fallbackUser);
        setIsBlocked(false);
      } finally {
        setIsLoadingRole(false);
      }
    };

    if (isClerkLoaded) {
      loadUser();
    }
  }, [clerkUser, isClerkLoaded]);

  useEffect(() => {
    // Check for existing session (legacy admin token support)
    const token = localStorage.getItem("adminToken");
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      // Fetch user data
      api
        .get("/api/auth/me")
        .then((response) => {
          setUser(response.data);
        })
        .catch(() => {
          localStorage.removeItem("adminToken");
        });
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.post("/api/auth/login", { email, password });
    const { token, user } = response.data;
    localStorage.setItem("adminToken", token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(user);
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
  };

  // Get role and permissions from database (via user object)
  // Feature: 001-user-role-management (T039)
  // Security: Role and permissions come from backend database, not Clerk metadata
  const role = user?.role || null;
  const isAdmin = user?.isAdmin || false;
  const permissions = user?.permissions || [];

  // Calculate loading state
  // We're loading if Clerk is loaded, there's a Clerk user, but we haven't fetched the role yet
  const isLoading = isClerkLoaded && !!clerkUser && (isLoadingRole || !user);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
        isLoading,
        role,
        isAdmin,
        permissions,
        clerkUser,
        isBlocked,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
